<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    icon="mdi-cog"
    color="deep-orange"
    @close="handleClose"
  >
    <v-row>
      <!-- Left Column -->
      <v-col cols="12" md="6">
        <delivery-switches
          v-if="model"
          ref="deliverySwitches"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
        />

        <!-- Time Settings -->
        <time-settings-card
          v-model="localDeliveryTime"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
        />

        <!-- Limits -->
        <!-- <limits-card
          v-model="localLimits"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :price="localPrice"
          :can-order-extra="localCanOrderExtra"
          @save="save"
        /> -->
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" md="6">
        <!-- Menu Type -->
        <menu-type-card
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :menu-types="menuTypes"
        />

        <!-- Delivery Locations -->
        <delivery-locations-card
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :delivery-time="localDeliveryTime"
        />
        <!-- Week Days Schedule -->
        <week-schedule-card :restaurant="selectedRestaurant" />

        <slot></slot>
      </v-col>
    </v-row>

    <template #footer-actions>
      <primary-button color="deep-orange" :loading="saving" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        {{ $t("save") }}
      </primary-button>
    </template>
  </fullscreen-dialog>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasModel from "@/mixins/hasModel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import companyService from "@/services/company";
import {
  DeliveryLocationsCard,
  TimeSettingsCard,
  LimitsCard,
  MenuTypeCard,
  WeekScheduleCard,
  DeliverySwitches,
} from "./delivery-settings";

export default {
  name: "CompanyDeliverySettingsDialog",
  mixins: [hasModel, hasSnackbar],
  components: {
    FullscreenDialog,
    DeliveryLocationsCard,
    TimeSettingsCard,
    LimitsCard,
    MenuTypeCard,
    WeekScheduleCard,
    DeliverySwitches,
  },
  props: {
    selectedRestaurant: {
      type: Object,
      default: null,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
    menuTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localDeliveryTime: "no_delivery_time",
      localLimits: null,
      localPrice: 0,
      localCanOrderExtra: false,
      saving: false,
    };
  },
  computed: {
    dialogTitle() {
      const restaurant = this.toLatin(this.selectedRestaurant?.name || "");
      const company = this.toLatin(this.selectedCompany?.name || "");
      return `${restaurant} / ${company}`;
    },
  },
  watch: {
    selectedRestaurant: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initializeFromRestaurant();
        }
      },
    },
    localDeliveryTime(val) {},
  },
  methods: {
    save() {
      this.saving = true;
      companyService
        .setResturantOptions(
          this.selectedCompany.id,
          this.selectedRestaurant.id,
          this.selectedRestaurant.company_restaurant_settings
        )
        .then(() => {
          this.showSuccessSnackbar(this.$t("menu_type_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_updating_menu_type"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    initializeFromRestaurant() {
      if (!this.selectedRestaurant) return;

      // Normalize type_of_delivery — API returns [] when empty, needs to be an object
      if (
        !this.selectedRestaurant.company_restaurant_settings.type_of_delivery ||
        Array.isArray(
          this.selectedRestaurant.company_restaurant_settings.type_of_delivery
        )
      ) {
        this.$set(
          this.selectedRestaurant.company_restaurant_settings,
          "type_of_delivery",
          {
            take_by_user: false,
            restaurant_eat: false,
            delivery_to_firm: false,
            delivery_to_user_address: false,
          }
        );
      }

      // Initialize limits from voucher
      if (this.selectedRestaurant.voucher) {
        const limitType = this.selectedRestaurant.voucher.params?.limit?.type;
        if (limitType === "meals_per_day") {
          this.localLimits = "obrok";
        } else if (limitType === "meals_per_shift") {
          this.localLimits = "smena";
        } else if (limitType === "amount") {
          this.localLimits = "firma";
        } else if (limitType === "bon") {
          this.localLimits = "bon";
        } else if (limitType === "student") {
          this.localLimits = "student";
        }
        this.localCanOrderExtra =
          this.selectedRestaurant.voucher.params?.limit?.can_order_extra || false;
      }

      // Initialize delivery settings
      if (this.selectedRestaurant.delivery_setting) {
        this.localDeliveryTime =
          this.selectedRestaurant.delivery_setting.delivery || "no_delivery_time";
      } else {
        this.localDeliveryTime = "no_delivery_time";
      }
    },
    async handleClose() {
      this.model = false;
      this.$emit("close");
    },
  },
};
</script>

<style>
/* Radio button alignment - unscoped for Vuetify components */
.fullscreen-dialog .v-radio {
  align-items: center !important;
}

.fullscreen-dialog .v-radio .v-input--selection-controls__input {
  align-self: center !important;
  margin-top: 0 !important;
}

.fullscreen-dialog .v-radio .v-label {
  display: flex;
  align-items: center !important;
}

.fullscreen-dialog .v-input--selection-controls__ripple {
  top: calc(50% - 24px) !important;
}
</style>
